import { Component } from '@theme/component';

/**
 * PIMLO announcement bar. Every offer is visible on desktop; mobile rotates.
 * Uses the theme's real content_for blocks so Shopify theme-editor updates appear.
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #interval = undefined;
  #desktop = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#desktop.addEventListener('change', this.#handleViewportChange);
    this.#handleViewportChange();
  }

  disconnectedCallback() {
    this.suspend();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#desktop.removeEventListener('change', this.#handleViewportChange);
    super.disconnectedCallback();
  }

  #handleViewportChange = () => {
    this.suspend();
    if (this.#desktop.matches) {
      this.refs.slides?.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
    } else {
      this.current = this.#current;
      this.play();
    }
  };

  next() {
    if (!this.#desktop.matches) this.current += 1;
  }

  previous() {
    if (!this.#desktop.matches) this.current -= 1;
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.#desktop.matches || (this.refs.slides?.length ?? 0) < 2) return;
    this.suspend();
    this.paused = false;
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.paused || this.#desktop.matches) return;
    this.play();
  };

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const value = parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(value) ? undefined : value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(value) {
    this.#current = value;
    const slides = this.refs.slides ?? [];
    if (this.#desktop.matches) {
      slides.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
      return;
    }
    const length = slides.length;
    if (!length) return;
    const activeIndex = ((value % length) + length) % length;
    slides.forEach((slide, index) => slide.setAttribute('aria-hidden', String(index !== activeIndex)));
  }

  #handleVisibilityChange = () => {
    if (document.hidden) this.suspend();
    else this.resume();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
